/**
 * Hook for preloading and crossfading music tracks
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { logger } from '@/lib/logger';

interface Track {
  name: string;
  artist: string;
  preview_url?: string;
  spotify_uri?: string;
}

interface PreloadOptions {
  crossfadeDuration?: number; // in seconds
  preloadCount?: number; // how many tracks to preload
}

export function useMusicPreload(options: PreloadOptions = {}) {
  const {
    crossfadeDuration = 3,
    preloadCount = 2
  } = options;

  const [isPreloading, setIsPreloading] = useState(false);
  const [preloadedTracks, setPreloadedTracks] = useState<Set<string>>(new Set());
  const [isCrossfading, setIsCrossfading] = useState(false);

  const audioCache = useRef<Map<string, HTMLAudioElement>>(new Map());
  const currentAudio = useRef<HTMLAudioElement | null>(null);
  const nextAudio = useRef<HTMLAudioElement | null>(null);
  const crossfadeInterval = useRef<NodeJS.Timeout | null>(null);

  // Preload track audio
  const preloadTrack = useCallback(async (track: Track) => {
    if (!track.preview_url) return false;
    
    const key = `${track.name}-${track.artist}`;
    
    if (preloadedTracks.has(key)) {
      return true;
    }

    try {
      setIsPreloading(true);
      
      const audio = new Audio(track.preview_url);
      audio.preload = 'auto';
      
      await new Promise((resolve, reject) => {
        audio.oncanplaythrough = resolve;
        audio.onerror = reject;
        audio.load();
      });

      audioCache.current.set(key, audio);
      setPreloadedTracks(prev => new Set([...prev, key]));
      
      logger.info('Track preloaded', { track: track.name });
      return true;
    } catch (error) {
      logger.error('Failed to preload track', error, { track: track.name });
      return false;
    } finally {
      setIsPreloading(false);
    }
  }, [preloadedTracks]);

  // Preload multiple tracks
  const preloadTracks = useCallback(async (tracks: Track[]) => {
    const tracksToPreload = tracks.slice(0, preloadCount);
    await Promise.all(tracksToPreload.map(preloadTrack));
  }, [preloadCount, preloadTrack]);

  // Get cached audio
  const getCachedAudio = useCallback((track: Track): HTMLAudioElement | null => {
    const key = `${track.name}-${track.artist}`;
    return audioCache.current.get(key) || null;
  }, []);

  // Start crossfade between current and next track
  const startCrossfade = useCallback((
    current: HTMLAudioElement,
    next: HTMLAudioElement
  ) => {
    setIsCrossfading(true);
    
    const steps = 30; // 30 steps for smooth fade
    const stepDuration = (crossfadeDuration * 1000) / steps;
    let currentStep = 0;

    if (crossfadeInterval.current) {
      clearInterval(crossfadeInterval.current);
    }

    // Start next track
    next.volume = 0;
    next.play().catch(err => {
      logger.error('Failed to play next track during crossfade', err);
    });

    crossfadeInterval.current = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      // Fade out current
      current.volume = Math.max(0, 1 - progress);
      
      // Fade in next
      next.volume = Math.min(1, progress);

      if (currentStep >= steps) {
        if (crossfadeInterval.current) {
          clearInterval(crossfadeInterval.current);
        }
        
        // Stop and cleanup current track
        current.pause();
        current.currentTime = 0;
        
        setIsCrossfading(false);
        logger.info('Crossfade completed');
      }
    }, stepDuration);

  }, [crossfadeDuration]);

  // Play track with crossfade if possible
  const playWithCrossfade = useCallback(async (
    track: Track,
    nextTrack?: Track
  ): Promise<boolean> => {
    const audio = getCachedAudio(track);
    
    if (!audio) {
      logger.warn('Track not preloaded, playing without crossfade', { track: track.name });
      return false;
    }

    // If there's a current track, crossfade
    if (currentAudio.current && currentAudio.current !== audio) {
      startCrossfade(currentAudio.current, audio);
      currentAudio.current = audio;
    } else {
      // First track, just play
      audio.volume = 1;
      try {
        await audio.play();
        currentAudio.current = audio;
      } catch (error) {
        logger.error('Failed to play track', error);
        return false;
      }
    }

    // Preload next track if provided
    if (nextTrack) {
      nextAudio.current = getCachedAudio(nextTrack);
      if (!nextAudio.current) {
        preloadTrack(nextTrack);
      }
    }

    return true;
  }, [getCachedAudio, startCrossfade, preloadTrack]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (crossfadeInterval.current) {
        clearInterval(crossfadeInterval.current);
      }
      
      // Stop and cleanup all audio
      audioCache.current.forEach(audio => {
        audio.pause();
        audio.src = '';
      });
      audioCache.current.clear();
    };
  }, []);

  return {
    preloadTrack,
    preloadTracks,
    playWithCrossfade,
    getCachedAudio,
    isPreloading,
    isCrossfading,
    preloadedCount: preloadedTracks.size,
  };
}
